/**
 * to navigate through application routes use
 * @example: app.navigate('landingpage', {optional} options);
 */
import app from 'app';
import PictureInPictureAdapter from 'c/PictureInPictureAdapter';
import AssetModal from 'v/assetModal/AssetModal.vue';
import AssetEditorStore from 'AssetEditor/store/AssetEditorStore';
import RestrictionsController from 'c/RestrictionsController';
import storyViewBus from 'v/Story/storyViewBus';

app.namespace.Controller.AssetController = class AssetController {
  static get NON_SERIALIZABLE_KEYS() {
    return [
      'limit',
      // 'guid', - commented out so serialized models could be searched by guid
      'portrait',
      'originalEvent',
    ];
  }

  static parse(asset, options) {
    options = _.options(options, {
      generateId: true,
    });

    if (asset._parsed) {
      return asset;
    }

    if (_.isEmpty(asset.metadata)) {
      asset.metadata = {};
    }
    if (_.isEmpty(asset.agd)) {
      asset.agd = {};
    }

    if (app.assetType.isFRAGMENT(asset)) {
      if (!_.isDefined(asset.agd.videoSpeed)) {
        asset.agd.videoSpeed = 1;
      }
    }

    if (_.isUndefined(asset.pinned)) {
      asset.pinned = false;
    }

    if (options.generateId && _.isEmpty(asset.id)) {
      asset.id = app.generateAssetId(asset);
    }
    if (asset.metadata.providerAssetId) {
      asset._providerId = `${asset.metadata.providerAssetId}_${asset.metadata.providerId}`;
    }

    app.prependHttpAcrossObject(asset);
    if (app.assetType.isVIDEO(asset) && _.isEmpty(asset.metadata.posterUrl)) {
      asset.metadata.posterUrl = app.getAppLocation() + app.DEFAULT.videoPoster;
    }

    if (
      app.assetType.isINSTAGRAM(asset) &&
      _.isEmpty(asset.metadata.socialUserName)
    ) {
      asset.metadata.socialUserName = asset.metadata.socialUserScreenName;
      asset.metadata.socialUserProfileImageUrl =
        'http://assetsprod-a.akamaihd.net/454d3522_c1d4_410e_8a43_9bfaf62ba70a_asset_server/uploadedFile.png';
    }

    if (
      !_.isDefined(asset.portrait) &&
      asset.metadata.width > 0 &&
      asset.metadata.height > 0
    ) {
      asset.portrait = asset.metadata.height >= asset.metadata.width;
    }

    if (
      _.isUndefined(asset.webpageMediaTypeName) ||
      asset.webpageMediaTypeName.toLowerCase() === 'unknown'
    ) {
      asset.webpageMediaTypeName = asset.metadata.title;
    }

    if (_.isUndefined(asset.realTimePreviewChecksum)) {
      asset.realTimePreviewChecksum = null;
    }

    if (_.isUndefined(asset.realTimePreviewResultData)) {
      asset.realTimePreviewResultData = null;
    }

    asset.dateSelected = asset.dateSelected || null;
    asset._previewOp = null;

    RestrictionsController.assignRestrictions(asset);

    asset._parsed = true;
    return asset;
  }

  static makeVideoFragment(asset) {
    Object.assign(asset, {
      type: app.assetType.FRAGMENT,
      startTime: app.DEFAULT.videoDurationPadding,
      endTime: app.sfp(
        asset.metadata.duration - app.DEFAULT.videoDurationPadding
      ),
      thumbnail: null,
    });
    asset.id = app.generateAssetId(asset);
    asset.metadata.assetType = 'VIDEO_SEGMENT';
  }

  static serializeAsset(asset) {
    const rv = {};

    _.each(asset, (value, key) => {
      if (
        !/^[_|$]/.test(key) &&
        !_.includes(AssetController.NON_SERIALIZABLE_KEYS, key)
      ) {
        rv[key] = value;
      }
    });

    if (!PictureInPictureAdapter.isEmpty(rv)) {
      rv[PictureInPictureAdapter.TRACK_PROP] = _.map(
        rv[PictureInPictureAdapter.TRACK_PROP],
        (overlay) => {
          return AssetController.serializeAsset(overlay);
        }
      );
    }

    return _.cloneDeepAndClean(rv);
  }

  static serialize(assets) {
    return assets.map((asset) => this.serializeAsset(asset));
  }

  static previewLightboxAsset(asset, filteredAssets) {
    const getAssets = () => {
      const assetsArray =
        filteredAssets || app.rootStore.getters.selectedAssets;
      return _.filter(assetsArray, (asset) => {
        return !app.assetType.isWALLPAPER(asset);
      });
    };

    const store = new AssetEditorStore(
      {
        openedFrom: 'lightbox',
        currentPageId: null,
        ian: {
          getAssets,
          loop: true,
        },
        timeInAsset: 0,
        isStoryboarder: app.rootStore.getters['user/isTemplateSpecialist'],
      },
      {
        storyModel: app.storyController.story,
        asset,
      }
    );

    app.rootStore.registerModule('assetEditor', store);
    const $dialog = app.showModal({
      type: 'custom',
      wrapperClass: ['wt2-asset-modal', 'preview'],
      closeOnClickOut: false,
      showCloseButton: false,
      component: AssetModal,
      store: app.rootStore,
      componentPropsData: {},
    });
    $dialog.$on('close', (data) => {
      storyViewBus.$emit('lightboxAssetPreviewClosed', {
        asset: store.state.workModel,
      });
    });
  }

  static editTimelineAsset(options) {
    options = Object.assign(
      {
        e: null, // user event
        asset: null,
        getAssets: () => app.timelineGateway.getVisualData(),
        overlayGuid: null, // what overlay to preselect
        currentPageId: null, // what effect page to show
      },
      options
    );

    if (options.e) {
      const collageLevel = options.overlayGuid ? 'overlay' : 'BG asset';
      const using =
        options.e.originalEvent.type === 'dblclick' ? 'doubleclick' : 'menu';
      app.analytics('analytics.scenes.openSceneEditor', {
        using,
        'collage level': collageLevel,
      });
    }

    const store = new AssetEditorStore(
      {
        openedFrom: 'timeline',
        currentPageId: options.currentPageId || 'preview',
        ian: {
          getAssets: options.getAssets,
        },
        timeInAsset: 0,
        isStoryboarder: app.rootStore.getters['user/isTemplateSpecialist'],
      },
      {
        storyModel: app.storyController.story,
        asset: options.asset,
      }
    );
    const openedAssetGuid = options.asset?.guid;
    let closedAssetGuid = null;

    app.rootStore.registerModule('assetEditor', store);
    app
      .showModal({
        type: 'custom',
        wrapperClass: ['wt2-asset-modal'],
        closeOnClickOut: false,
        closeOnEscape: false,
        showCloseButton: false,
        component: AssetModal,
        store: app.rootStore,
        componentPropsData: {
          overlayGuid: options.overlayGuid,
        },
      })
      .$on('close', (e) => {
        closedAssetGuid = store.state.workModel?.guid;
        if (openedAssetGuid !== closedAssetGuid) {
          storyViewBus.$emit('lightboxAssetPreviewClosed', {
            asset: store.state.workModel,
          });
        }
      });
  }

  static checkIfNavigatedToAssetEditor() {
    const location = window.location.pathname;
    const patternMatch = app.checkAssetEditorUrl(location);

    if (patternMatch) {
      const assetId = patternMatch[1];
      const displayStartTime = patternMatch[2];
      let currentEffect = patternMatch[3];

      if (currentEffect === 'overlay_asset') {
        currentEffect = 'preview';
      }

      const assets = app.timelineGateway.getVisualData();

      let visualTrack = assets.find(
        (asset) =>
          asset.displayStartTime === +displayStartTime && asset.id === assetId
      );

      if (!visualTrack) {
        const assetsById = assets.filter((asset) => asset.id === assetId);
        if (assetsById.length === 1) {
          visualTrack = assetsById[0];
        }
      }

      if (visualTrack) {
        AssetController.editTimelineAsset({
          asset: visualTrack,
          currentPageId: currentEffect,
        });
      } else {
        console.warn('Unknown visualTrack to init asset editor!', {
          assetId,
          displayStartTime,
          currentEffect,
        });

        app.navigate('/stories/' + app.storyController.story.id, {
          trigger: false,
        });
      }
    }
  }

  static getSearchContent(asset) {
    const output = [];
    const fields = [
      'title',
      'caption',
      'transcript',
      'socialUserName',
      'socialUserScreenName',
    ];
    for (const field of fields) {
      if (!_.isEmpty(asset.metadata[field])) {
        output.push(asset.metadata[field].replace(/["']/g, ''));
      }
    }
    return output;
  }

  static isMatchesSearchCriteria(asset, rgPattern) {
    const searchContent = AssetController.getSearchContent(asset).join('\n');
    if (!_.isEmpty(rgPattern)) {
      rgPattern = rgPattern.replace(/["']/g, '');
    }
    return (
      null == rgPattern || app.string2RegExp(rgPattern, 'i').test(searchContent)
    );
  }

  static matchDateFrom(asset, dateFromValue) {
    let isMatch = true;
    const publicationDate = asset.metadata.publicationDate;
    if (dateFromValue && !app.assetType.isINFOGRX(asset)) {
      isMatch = publicationDate && publicationDate >= dateFromValue;
    }
    return isMatch;
  }

  static matchDateTo(asset, dateToValue) {
    let isMatch = true;
    const publicationDate = asset.metadata.publicationDate;
    if (dateToValue && !app.assetType.isINFOGRX(asset)) {
      isMatch = publicationDate && publicationDate <= dateToValue;
    }
    return isMatch;
  }

  static hasExpirationDate(asset) {
    const expirationDate = asset.metadata.expirationDate;
    if (
      _.isUndefined(expirationDate) ||
      Object.getOwnPropertyDescriptor(
        tk.context._expirationExcludeProviders,
        asset.metadata.providerId
      )
    ) {
      return false;
    }
    // If expiration date is more than 10 years from now, it should not be displayed to the user at all.
    const deltaYears = app.moment(expirationDate).diff(app.moment(), 'years');
    return deltaYears < 10;
  }

  static isAssetRestricted(asset) {
    return (
      !_.isEmpty(asset.metadata.restrictions) ||
      AssetController.hasExpirationDate(asset)
    );
  }

  static matchRestriction(asset, restricted) {
    if (restricted) {
      return AssetController.isAssetRestricted(asset);
    } else {
      return !AssetController.isAssetRestricted(asset);
    }
  }

  static matchResolution(asset, res, val) {
    let isMatch = false;
    const w = asset.metadata.width;

    if (!app.assetType.isVIDEO()) {
      // images are not resolution filtered
      return true;
    }
    if (val && app.assetType.isVIDEO()) {
      switch (res) {
        case '480P':
          isMatch = w > 0 && w <= 719;
          break;
        case '720P':
          isMatch = w >= 720 && w <= 1079;
          break;
        case '1080P':
          isMatch = w >= 1080;
          break;
      }
    }
    return isMatch;
  }

  static matchContentFamily(asset, contentFamily) {
    // Editorial_and_Creative ,Editorial,Creative
    return contentFamily.indexOf(asset.metadata.contentFamily) >= 0; // == contentFamily;
  }

  static matchProvider(asset, providersStr) {
    return providersStr.includes(asset.metadata.providerId);
  }

  static matchOrientation(asset, orientationsStr) {
    const orientation = asset.metadata.orientation;

    if (orientation) {
      return orientationsStr.includes(asset.metadata.orientation);
    }

    return false;
  }

  static matchQuality(asset, qualityStr) {
    const assetHeight = asset.metadata.height;
    if (!assetHeight) {
      return false;
    }

    const shouldInclude480 = qualityStr === 'RES480p';
    const shouldInclude720 = qualityStr === 'RES720p';
    const shouldInclude1080 = qualityStr === 'RES1080p';

    if (assetHeight <= 480 && shouldInclude480) {
      return true;
    } else if (assetHeight > 640 && shouldInclude720) {
      return true;
    } else if (assetHeight > 1280 && shouldInclude1080) {
      return true;
    }

    return false;
  }

  static getVideoPosterUrl(asset) {
    asset = (asset && asset.attributes) || asset;
    let rv = app.DEFAULT.storyThumbnailImage;

    if (!asset || !asset.metadata) {
      // noop
    } else if (
      app.assetType.isFRAGMENT(asset) &&
      asset.previewResultData &&
      asset.previewResultData.thumbnail
    ) {
      rv = app.prependHttp(asset.previewResultData.thumbnail);
    } else if (
      asset.metadata.posterUrl &&
      (app.assetType.isFRAGMENT(asset) || app.assetType.isGIF(asset))
    ) {
      rv = app.prependHttp(asset.metadata.posterUrl);
    } else if (asset.metadata.urlCdn) {
      rv = app.prependHttp(asset.metadata.urlCdn);
    } else if (asset.metadata.url) {
      rv = app.prependHttp(asset.metadata.url);
    }

    return rv;
  }

  static isAssetSelected(asset) {
    if (asset._providerId) {
      return !!app.rootStore.state.story.selectedAssets.find((item) => {
        return item._providerId === asset._providerId;
      });
    } else {
      return !!app.rootStore.state.story.selectedAssets.find((item) => {
        return item.metadata.assetUUID === asset.metadata.assetUUID;
      });
    }
  }

  static deletePrivatelyUploadedAsset(asset) {
    return new Promise((resolve, reject) => {
      const isSelected =
        app.namespace.Controller.AssetController.isAssetSelected(asset);
      const confirmed = new _.ColdPromise();
      const needConfirmation =
        isSelected && !app.remoteStash.get('doNotShowDeleteAssetConfirm');

      if (needConfirmation) {
        app.dialog
          .confirm(
            {
              type: app.dialog.types.CONFIRM_3,
              title: 'Delete',
              description:
                'This will delete the item from your uploads and from your selected media.',
              buttonYesText: 'OK',
              showCheckbox: true,
            },
            {
              // NOTE: workaround, specifying custom container since dialog may be shown
              // above old modal design
              appendIdentifier: 'body',
              wrapperStyle: { 'z-index': 2055 },
            }
          )
          .then((data) => {
            if (data.action) {
              if (data?.metadata?.checkboxValue) {
                app.remoteStash.set('doNotShowDeleteAssetConfirm', true);
              }
              confirmed.resolve();
            }
          });
      } else {
        confirmed.resolve();
      }

      confirmed.then(() => {
        app.rootStore.dispatch('ASSET_DELETE', asset.metadata.assetUUID);
        if (isSelected) {
          app.rootStore.dispatch('UNSELECT_ALL_SIMILAR', asset);
        }
        resolve();
      });
    });
  }

  static setInitialAssetDuration(asset, duration) {
    const isImage = app.assetType.isIMAGE(asset);
    const isWallpaper = app.assetType.isWALLPAPER(asset);
    const isSocial = app.assetType.isSOCIAL(asset);
    const isSocialFragment = app.assetType.isSOCIALFRAGMENT(asset);
    const isRelevant =
      isImage || isWallpaper || (isSocial && !isSocialFragment);

    if (isRelevant) {
      app.storyController.story.remoteStash.set(
        isImage
          ? 'image-duration'
          : isWallpaper
          ? 'wallpaper-duration'
          : 'social-duration',
        duration
      );
    }
  }

  static getInitialAssetDuration(asset) {
    let rv = null;
    const isImage = app.assetType.isIMAGE(asset);
    const isWallpaper = app.assetType.isWALLPAPER(asset);
    const isSocial = app.assetType.isSOCIAL(asset);
    const isSocialFragment = app.assetType.isSOCIALFRAGMENT(asset);
    const isRelevant =
      isImage || isWallpaper || (isSocial && !isSocialFragment);

    if (isRelevant) {
      rv = app.storyController.story.remoteStash.get(
        isImage
          ? 'image-duration'
          : isWallpaper
          ? 'wallpaper-duration'
          : 'social-duration'
      );
    }

    return rv;
  }
};

export default app.namespace.Controller.AssetController;
